import {
  IProviderShareableProfile,
  StateInfoType,
} from '@sondermind/utilities/models-public-facing';

/**
 * Returns the states a provider can be surfaced under on the PSL, as state
 * objects ({@link StateInfoType} — `{ abbrev, name }`).
 *
 * A state is "valid" only when the provider holds an ACTIVE licensure there
 * (`provider_licensures[].state_code`) AND has a location in that state AND is
 * credentialed there. This is the biz logic the Jira AC calls "licensed (active
 * licensure, NOT certification)". It was historically implemented inline on the
 * landing-page header under the misleading name
 * `getLocationStatesWithCertifications` even though it reads licensures, not
 * certifications — extracted here so the header and footer share one source of
 * truth (YOR-1628).
 *
 * This computes the intersection once. Callers pick the shape they need via the
 * {@link getValidStateNames} / {@link getValidStateAbbrevs} helpers.
 */
export function getValidStates(profile: IProviderShareableProfile): StateInfoType[] {
  const licensureStates = new Set(
    (profile.provider_licensures ?? []).map((lic) => lic.state_code)
  );
  const locationStates = new Set(
    (profile.locations ?? []).map((loc) => loc.state)
  );
  const credentialedStates = profile.credentialed_states ?? [];

  // Intersection of all three sets. The credentialed_states entries carry both
  // abbrev and name, so we keep the whole object and let callers map.
  return credentialedStates.filter(
    (state) => licensureStates.has(state.abbrev) && locationStates.has(state.abbrev)
  );
}

/**
 * Returns the full state names a provider is valid to be surfaced under
 * (active licensure ∩ location ∩ credentialed). Thin wrapper over
 * {@link getValidStates}.
 */
export function getValidStateNames(profile: IProviderShareableProfile): string[] {
  return getValidStates(profile).map((state) => state.name);
}

/**
 * Returns the state abbreviations a provider is valid to be surfaced under
 * (active licensure ∩ location ∩ credentialed). Thin wrapper over
 * {@link getValidStates}. Used by the PSL footer, which only supports a single
 * state.
 */
export function getValidStateAbbrevs(profile: IProviderShareableProfile): string[] {
  return getValidStates(profile).map((state) => state.abbrev);
}
